import {
  orgRole,
  projectRole,
  type InvitationView,
  type Org,
  type OrgMember,
  type OrgProject,
  type OrgRole,
  type ProjectRole,
} from '@p2b/contract';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ErrorNote } from '../../App.js';
import { api } from '../../api.js';
import { formatDay, label, useI18n } from '../../i18n.js';
import { Icon } from '../../ui.js';
import {
  fill,
  filterPeople,
  initials,
  invitableProjects,
  parseEmails,
  peopleRows,
  projectsCell,
  PeopleEmpty,
  relativeDay,
  type PeopleFilter,
} from '../orgParts.js';
import { Dialog, RowMenu, Segmented } from './widgets.js';

/** The search and filter appear once the table is long enough to need them. */
const TOOLBAR_FROM = 6;

/** Members and the invitations still waiting, in one table (owner's layout, 2026-11-01). */
export function MembersTab({
  org,
  members,
  invitations,
  isAdmin,
  isOwner,
  meId,
  projectCount,
  onInvite,
}: {
  org: Org;
  projectCount: number;
  members: OrgMember[];
  invitations: InvitationView[];
  isAdmin: boolean;
  isOwner: boolean;
  meId?: string;
  onInvite?: () => void;
}) {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<PeopleFilter>('all');
  const [note, setNote] = useState<string | null>(null);
  const [changing, setChanging] = useState<OrgMember | null>(null);
  const [removing, setRemoving] = useState<OrgMember | null>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['org', org.slug] });
    void qc.invalidateQueries({ queryKey: ['invitations', org.slug] });
  };
  const resend = useMutation({
    mutationFn: (id: string) => api.resendInvitation(org.slug, id),
    onSuccess: () => {
      setNote(t('resent'));
      refresh();
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.revokeInvitation(org.slug, id),
    onSuccess: refresh,
  });

  const rows = peopleRows(members, invitations);
  if (rows.length === 0) return <PeopleEmpty onInvite={onInvite} />;
  const shown = filterPeople(rows, q, filter);
  const invitedCount = rows.filter((r) => r.kind === 'invitation').length;

  return (
    <section>
      {rows.length >= TOOLBAR_FROM && (
        <div className="toolbar">
          <label className="search grow-sm">
            <Icon.Search className="search-icon" />
            <input
              type="search"
              aria-label={t('searchPeople')}
              placeholder={t('searchPeople')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </label>
          {invitedCount > 0 && (
            <Segmented
              label={t('status')}
              value={filter}
              onChange={setFilter}
              options={[
                { value: 'all', label: t('allPeople'), count: rows.length },
                { value: 'active', label: t('active'), count: members.length },
                { value: 'invited', label: t('invitations'), count: invitedCount },
              ]}
            />
          )}
        </div>
      )}
      <ErrorNote error={resend.error ?? revoke.error} />
      {note && <p className="sent">{note}</p>}
      {shown.length === 0 ? (
        <p className="empty">{t('noMatch')}</p>
      ) : (
        <div className="sheet sheet-menus">
          <table className="stack-sm org-table">
            <thead>
              <tr>
                <th>{t('name')}</th>
                <th>{t('role')}</th>
                {isAdmin && <th className="hide-sm">{t('projects')}</th>}
                <th>{t('status')}</th>
                {isAdmin && <th className="hide-sm">{t('lastSignIn')}</th>}
                {isAdmin && (
                  <th>
                    <span className="sr-only">{t('rowActions')}</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => {
                if (r.kind === 'member') {
                  const m = r.member;
                  const ownerRow = m.role === 'owner';
                  const canAct = isAdmin && m.userId !== meId && (isOwner || !ownerRow);
                  return (
                    <tr key={r.key}>
                      <td className="cell-grow">
                        <div className="who">
                          <span className="avatar-sm" aria-hidden="true">
                            {initials(m.name, m.email)}
                          </span>
                          <div className="who-text">
                            <div className="cell-strong">{m.name || m.email}</div>
                            {m.name && <div className="sub break">{m.email}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="nowrap">{label(t, 'role', m.role)}</td>
                      {isAdmin && (
                        <td className="hide-sm sub">
                          {projectsCell(
                            (m.projects ?? []).map((p) => p.name),
                            projectCount,
                            t('allProjects'),
                          )}
                        </td>
                      )}
                      <td>
                        <span className="tag dot ok">{t('memberActive')}</span>
                      </td>
                      {isAdmin && (
                        <td className="hide-sm nowrap">
                          {m.lastSignInAt ? (
                            relativeDay(m.lastSignInAt, lang, t)
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                      )}
                      {isAdmin && (
                        <td className="actions">
                          {canAct && (
                            <RowMenu label={`${t('rowActions')}: ${m.name || m.email}`}>
                              <button className="menu-item" onClick={() => setChanging(m)}>
                                {t('changeRole')}
                              </button>
                              <hr className="rule" />
                              <button className="menu-item danger" onClick={() => setRemoving(m)}>
                                {t('removeFromOrg')}
                              </button>
                            </RowMenu>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                }
                const i = r.invitation;
                const expired = i.status === 'expired';
                return (
                  <tr key={r.key}>
                    <td className="cell-grow">
                      <div className="who">
                        <span className="avatar-sm invited" aria-hidden="true">
                          @
                        </span>
                        <div className="who-text">
                          <div className="cell-strong break">{i.email}</div>
                          <div className="sub">
                            {expired
                              ? fill(t('expiredOn'), { date: formatDay(i.expiresAt, lang) })
                              : fill(t('invitedByUntil'), {
                                  who: i.inviterName ?? 'Mynstra',
                                  date: formatDay(i.expiresAt, lang),
                                })}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="nowrap">{label(t, 'role', i.role)}</td>
                    {isAdmin && (
                      <td className="hide-sm sub">
                        {projectsCell(
                          i.grants.map((g) => g.projectName ?? ''),
                          Number.POSITIVE_INFINITY,
                          t('allProjects'),
                        )}
                      </td>
                    )}
                    <td>
                      <span className="tag dot warn">
                        {expired ? t('invitationExpired') : t('invitationSent')}
                      </span>
                    </td>
                    {isAdmin && (
                      <td className="hide-sm">
                        <span className="muted">—</span>
                      </td>
                    )}
                    {isAdmin && (
                      <td className="actions">
                        <RowMenu label={`${t('rowActions')}: ${i.email}`}>
                          <button className="menu-item" onClick={() => resend.mutate(i.id)}>
                            {t('resend')}
                          </button>
                          {!expired && (
                            <button className="menu-item danger" onClick={() => revoke.mutate(i.id)}>
                              {t('revoke')}
                            </button>
                          )}
                        </RowMenu>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {changing && (
        <RoleDialog org={org} member={changing} isOwner={isOwner} onClose={() => setChanging(null)} />
      )}
      {removing && <RemoveDialog org={org} member={removing} onClose={() => setRemoving(null)} />}
    </section>
  );
}

function RoleDialog({
  org,
  member,
  isOwner,
  onClose,
}: {
  org: Org;
  member: OrgMember;
  isOwner: boolean;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [role, setRole] = useState<OrgRole>(member.role);
  const save = useMutation({
    mutationFn: () => api.setMemberRole(org.slug, member.userId, { role }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['org', org.slug] });
      onClose();
    },
  });
  return (
    <Dialog
      title={`${t('changeRole')}: ${member.name || member.email}`}
      onClose={onClose}
      onSubmit={() => save.mutate()}
      footer={
        <>
          <button type="button" className="quiet" onClick={onClose}>
            {t('cancel')}
          </button>
          <button className="primary" disabled={save.isPending || role === member.role}>
            {t('save')}
          </button>
        </>
      }
    >
      <label>
        {t('orgRole')}
        <select value={role} onChange={(e) => setRole(e.target.value as OrgRole)}>
          {orgRole.options
            .filter((r) => isOwner || r !== 'owner')
            .map((r) => (
              <option key={r} value={r}>
                {label(t, 'role', r)}
              </option>
            ))}
        </select>
      </label>
      <ErrorNote error={save.error} />
    </Dialog>
  );
}

function RemoveDialog({ org, member, onClose }: { org: Org; member: OrgMember; onClose: () => void }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: () => api.removeMember(org.slug, member.userId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['org', org.slug] });
      onClose();
    },
  });
  return (
    <Dialog
      title={t('removeFromOrg')}
      onClose={onClose}
      onSubmit={() => remove.mutate()}
      footer={
        <>
          <button type="button" className="quiet" onClick={onClose}>
            {t('cancel')}
          </button>
          <button className="danger-button" disabled={remove.isPending}>
            {t('remove')}
          </button>
        </>
      }
    >
      <p>{fill(t('removeConfirm'), { who: member.name || member.email, org: org.name })}</p>
      <ErrorNote error={remove.error} />
    </Dialog>
  );
}

/** Invite one or more addresses, with a role and any of this organization's projects (managed by the inviter). */
export function InviteDialog({
  org,
  projects,
  onClose,
}: {
  org: Org;
  projects: OrgProject[];
  onClose: () => void;
}) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const offered = invitableProjects(projects, org.slug);
  const [text, setText] = useState('');
  const [role, setRole] = useState<OrgRole>('member');
  const [picked, setPicked] = useState<Record<string, ProjectRole>>({});
  const [failed, setFailed] = useState<{ email: string; error: unknown }[]>([]);
  const { valid, invalid } = parseEmails(text);
  const send = useMutation({
    mutationFn: async () => {
      const grants = Object.entries(picked).map(([project, r]) => ({ project, role: r }));
      const results = await Promise.allSettled(
        valid.map((email) =>
          api.createInvitation(org.slug, { email, role, ...(grants.length ? { projects: grants } : {}) }),
        ),
      );
      return results
        .map((r, i) => (r.status === 'rejected' ? { email: valid[i]!, error: r.reason as unknown } : null))
        .filter((x): x is { email: string; error: unknown } => x !== null);
    },
    onSuccess: (fails) => {
      void qc.invalidateQueries({ queryKey: ['invitations', org.slug] });
      void qc.invalidateQueries({ queryKey: ['org', org.slug] });
      if (fails.length === 0) onClose();
      else {
        setFailed(fails);
        setText(fails.map((f) => f.email).join(', '));
      }
    },
  });
  const toggle = (id: string) =>
    setPicked((cur) => {
      const next = { ...cur };
      if (next[id]) delete next[id];
      else next[id] = 'client';
      return next;
    });

  return (
    <Dialog
      title={fill(t('inviteTo'), { org: org.name })}
      onClose={onClose}
      onSubmit={() => {
        setFailed([]);
        if (valid.length && !invalid.length) send.mutate();
      }}
      footer={
        <>
          <button type="button" className="quiet" onClick={onClose}>
            {t('cancel')}
          </button>
          <button className="primary" disabled={send.isPending || valid.length === 0 || invalid.length > 0}>
            {valid.length > 1 ? fill(t('sendInvites'), { n: valid.length }) : t('sendInvite')}
          </button>
        </>
      }
    >
      <label>
        {t('emails')}
        <textarea
          rows={2}
          required
          autoFocus
          placeholder={t('emailsHint')}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </label>
      {invalid.length > 0 && (
        <p className="field-error">{fill(t('badEmails'), { list: invalid.join(', ') })}</p>
      )}
      <label>
        {t('orgRole')}
        <select value={role} onChange={(e) => setRole(e.target.value as OrgRole)}>
          {orgRole.options.map((r) => (
            <option key={r} value={r}>
              {label(t, 'role', r)}
            </option>
          ))}
        </select>
      </label>
      {offered.length > 0 && (
        <fieldset className="pick-projects">
          <legend>
            {t('projects')} <span className="hint">{fill(t('onlyOrgProjects'), { org: org.name })}</span>
          </legend>
          <ul>
            {offered.map((p) => (
              <li key={p.id}>
                <label className="check">
                  <input type="checkbox" checked={Boolean(picked[p.id])} onChange={() => toggle(p.id)} />
                  {p.name}
                </label>
                <select
                  aria-label={`${t('projectRole')}: ${p.name}`}
                  disabled={!picked[p.id]}
                  value={picked[p.id] ?? 'client'}
                  onChange={(e) => setPicked({ ...picked, [p.id]: e.target.value as ProjectRole })}
                >
                  {projectRole.options.map((r) => (
                    <option key={r} value={r}>
                      {label(t, 'role', r)}
                    </option>
                  ))}
                </select>
              </li>
            ))}
          </ul>
        </fieldset>
      )}
      {failed.map((f) => (
        <div key={f.email}>
          <p className="field-error">{fill(t('inviteFailed'), { email: f.email })}</p>
          <ErrorNote error={f.error} />
        </div>
      ))}
    </Dialog>
  );
}
